import { useMemo, useState } from 'react';
import { Card, Stack, Group, Text, Badge, Button } from '@repletoryreach/sdk-ui';
import { Tabs } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import type { Doc, Endpoint } from '../spec';
import { headerParameters, pathParameters, queryParameters, readResponses, requestSchema } from '../spec';
import { buildRequest, initialValues, toCurl } from '../curl';
import type { RequestValues } from '../curl';
import { CodeBlock, MethodBadge } from './CodeBlock';
import { ParameterTable, SchemaTable } from './SchemaTable';
import { TryIt } from './TryIt';

/**
 * One endpoint: what it is, what it takes, what it answers, the curl for it,
 * and a way to run it.
 *
 * Collapsed until opened, because a reference of thirty endpoints all expanded
 * is a page nobody can find anything in, and because building the curl and the
 * schema tables for an endpoint nobody has looked at is work for nothing.
 */
export function OperationCard({
  endpoint,
  doc,
  baseUrl,
  apiKey
}: {
  endpoint: Endpoint;
  doc: Doc | undefined;
  baseUrl: string;
  apiKey: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('reference');
  const [values, setValues] = useState<RequestValues>(() => initialValues(endpoint));

  const curl = useMemo(
    () => toCurl(buildRequest(endpoint, baseUrl, values, apiKey || null)),
    [endpoint, baseUrl, values, apiKey]
  );

  const parameters = [
    ...pathParameters(endpoint.operation),
    ...queryParameters(endpoint.operation),
    ...headerParameters(endpoint.operation)
  ];
  const body = requestSchema(endpoint.operation);
  const responses = readResponses(endpoint.operation);

  return (
    <Card withBorder className="rr-dev__op" id={`op-${slug(endpoint.id)}`}>
      <Stack gap="sm">
        <button
          type="button"
          className="rr-dev__op-head"
          onClick={() => setOpen((was) => !was)}
          aria-expanded={open}
        >
          <span className="rr-dev__op-sig">
            <MethodBadge method={endpoint.method} />
            <code className="rr-dev__path">{endpoint.path}</code>
          </span>
          <span className="rr-dev__op-meta">
            {endpoint.deprecated && (
              <Badge size="sm" color="warning" variant="light">
                {t('developers.op.deprecated')}
              </Badge>
            )}
            <Badge size="sm" variant="light">
              {endpoint.scope}
            </Badge>
          </span>
        </button>

        <Text role="body">{endpoint.summary}</Text>

        {open && (
          <Stack gap="md">
            {endpoint.description && <Text role="bodySmall">{endpoint.description}</Text>}

            {endpoint.scopeNote && (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('developers.op.scopeNote', { note: endpoint.scopeNote })}
              </Text>
            )}

            <Tabs value={tab} onChange={setTab}>
              <Tabs.List>
                <Tabs.Tab value="reference">{t('developers.op.tab.reference')}</Tabs.Tab>
                <Tabs.Tab value="curl">{t('developers.op.tab.curl')}</Tabs.Tab>
                <Tabs.Tab value="try">{t('developers.op.tab.try')}</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="reference">
                <Stack gap="md">
                  {parameters.length > 0 ? (
                    <Stack gap="xs">
                      <Text role="label">{t('developers.op.parameters')}</Text>
                      <ParameterTable parameters={parameters} doc={doc} />
                    </Stack>
                  ) : (
                    <Text role="caption" c="var(--rr-color-text-muted)">
                      {t('developers.op.noParameters')}
                    </Text>
                  )}

                  {body && (
                    <Stack gap="xs">
                      <Text role="label">{t('developers.op.requestBody')}</Text>
                      <SchemaTable schema={body} doc={doc} />
                    </Stack>
                  )}

                  <Stack gap="xs">
                    <Text role="label">{t('developers.op.responses')}</Text>
                    {responses.map((response) => (
                      <Stack key={response.status} gap="xs" className="rr-dev__response">
                        <Group gap="sm" align="center" wrap="wrap">
                          <Badge
                            size="sm"
                            color={
                              response.status.startsWith('2')
                                ? 'success'
                                : response.status.startsWith('4')
                                  ? 'warning'
                                  : 'error'
                            }
                          >
                            {response.status}
                          </Badge>
                          <Text role="caption">{response.description}</Text>
                        </Group>
                        <SchemaTable schema={response.schema} doc={doc} />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="curl">
                <Stack gap="sm">
                  <CodeBlock
                    code={curl}
                    label={t('developers.op.tab.curl')}
                    caption={
                      apiKey.trim()
                        ? t('developers.op.curlWithKey')
                        : t('developers.op.curlNoKey')
                    }
                  />
                  {pathParameters(endpoint.operation).length > 0 && (
                    <Text role="caption" c="var(--rr-color-text-muted)">
                      {t('developers.op.curlPlaceholders')}
                    </Text>
                  )}
                  <Button size="xs" variant="subtle" onClick={() => setTab('try')}>
                    {t('developers.op.fillIn')}
                  </Button>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="try">
                <TryIt
                  endpoint={endpoint}
                  baseUrl={baseUrl}
                  values={values}
                  onValuesChange={setValues}
                  apiKey={apiKey}
                />
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

/** A DOM-safe id, so an endpoint can be linked to. */
function slug(id: string): string {
  return id.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
